/**
 * Speech utility — Web Speech API (nativo no browser, sem custo)
 * Suporta fila de falas, templates customizáveis e configurações persistidas.
 */
import { db, getCollectionName } from '../firebase';
import { doc, getDoc } from '../firestore-compat';

// ── Configuração padrão ────────────────────────────────────────────────────────
let speechConfig = {
  rate: 0.88,
  pitch: 1.05,
  volume: 1,
  ativo: true,
  templates: {
    novo_paciente:        'Operadora {convenio} solicitou avaliação do paciente {paciente}, restam {tempo} para vencer o SLA',
    visita_agendada:      'Avaliação de {paciente} agendada para {profissional}',
    status_change:        'Paciente {paciente} entrou em {status}',
    status_change_sla:         'Paciente {paciente} entrou em {status}, restam {tempo} para vencer o SLA',
    status_change_sla_vencido: 'Paciente {paciente} entrou em {status}. Atenção: SLA já vencido há {tempo}',
    avaliacao_confirmada: 'Avaliação de {paciente} foi confirmada por {profissional}',
    sla_alerta:           'Atenção, faltam {tempo} para vencer o SLA do Paciente {paciente}',
    sla_vencido:          'SLA do Paciente {paciente} vencido há {tempo}',
    implantado:           'Parabéns! Paciente {paciente} foi implantado com sucesso',
  },
};

// Expõe para SpeechConfig.js atualizar em runtime
window.__speechConfig = speechConfig;

/** Carrega configurações do Firestore (chamado uma vez na inicialização do app) */
export const loadSpeechConfig = async () => {
  try {
    const snap = await getDoc(doc(db, getCollectionName('configuracoes_v2'), 'speech'));
    if (snap.exists()) {
      const data = snap.data();
      const savedTemplates = data.templates || {};
      // Migração automática: se o template salvo não tem {paciente}, usa o padrão novo
      if (savedTemplates.novo_paciente && !savedTemplates.novo_paciente.includes('{paciente}')) {
        delete savedTemplates.novo_paciente;
      }
      // Migração: se o sla_alerta antigo usa "{paciente}" em atraso (formato antigo), reseta
      if (savedTemplates.sla_alerta && savedTemplates.sla_alerta.includes('em atraso')) {
        delete savedTemplates.sla_alerta;
      }
      speechConfig = {
        ...speechConfig,
        ...data,
        templates: { ...speechConfig.templates, ...savedTemplates },
      };
      window.__speechConfig = speechConfig;
    }
  } catch (e) {
    console.warn('SpeechUtils: falha ao carregar config:', e.message);
  }
};

// ── Fila de fala ──────────────────────────────────────────────────────────────
let queue = [];
let busy  = false;
let watchdog = null;

const resetBusy = () => {
  clearTimeout(watchdog);
  watchdog = null;
  busy = false;
};

const processQueue = () => {
  if (busy || queue.length === 0) return;

  // Verifica se avisos estão ativos (pode ter mudado em runtime)
  const cfg = window.__speechConfig || speechConfig;
  if (!cfg.ativo) { queue = []; return; }

  // Chrome bug: synthesis can get paused after tab switch — resume it first
  if (window.speechSynthesis.paused) window.speechSynthesis.resume();

  busy = true;
  const text = queue.shift();

  const u = new SpeechSynthesisUtterance(text);
  u.lang   = 'pt-BR';
  u.rate   = parseFloat(cfg.rate)   || 0.88;
  u.pitch  = parseFloat(cfg.pitch)  || 1.05;
  u.volume = parseFloat(cfg.volume) || 1;

  const tryVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const voiceName = (window.__speechConfig || speechConfig).voiceName;
    const preferred = voiceName ? voices.find(v => v.name === voiceName) : null;
    const ptVoice   = preferred
                   || voices.find(v => v.lang === 'pt-BR')
                   || voices.find(v => v.lang.startsWith('pt'));
    if (ptVoice) u.voice = ptVoice;
  };
  tryVoice();
  if (!u.voice) window.speechSynthesis.onvoiceschanged = tryVoice;

  u.onend   = () => { resetBusy(); processQueue(); };
  u.onerror = () => { resetBusy(); processQueue(); };

  // Watchdog: Chrome sometimes never fires onend/onerror — reset after 20s
  watchdog = setTimeout(() => {
    window.speechSynthesis.cancel();
    resetBusy();
    processQueue();
  }, 20000);

  window.speechSynthesis.speak(u);
};

// Chrome pauses synthesis when tab loses focus — resume on visibility restore
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
    }
  });
}

/** Aplica variáveis a um template */
const applyTemplate = (templateKey, vars = {}) => {
  const cfg = window.__speechConfig || speechConfig;
  let text = cfg.templates?.[templateKey] || speechConfig.templates[templateKey] || '';
  Object.entries(vars).forEach(([k, v]) => {
    text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v || '');
  });
  return text;
};

/**
 * Fala um texto em voz alta (enfileira se já estiver falando)
 */
export const speak = (text) => {
  const cfg = window.__speechConfig || speechConfig;
  if (!window.speechSynthesis) { console.warn('[Speech] speechSynthesis não suportado'); return; }
  if (!cfg.ativo) { console.warn('[Speech] avisos desativados (ativo=false) — texto ignorado:', text); return; }
  queue.push(text);
  processQueue();
};

/** Cancela tudo que está na fila e para a fala atual */
export const speakStop = () => {
  if (!window.speechSynthesis) return;
  queue = [];
  resetBusy();
  window.speechSynthesis.cancel();
};

// ── Eventos específicos (usam templates customizáveis) ────────────────────────

/** Novo paciente cadastrado — anuncia convênio, nome e tempo restante de SLA */
export const speakNovoPaciente = (convenio, paciente, tempo) =>
  speak(applyTemplate('novo_paciente', { convenio, paciente, tempo }));

/** Visita agendada */
export const speakVisitaAgendada = (paciente, profissional) =>
  speak(applyTemplate('visita_agendada', { paciente, profissional }));

/** Status avançou */
export const speakStatusChange = (paciente, status) =>
  speak(applyTemplate('status_change', { paciente, status }));

/** Status avançou com tempo restante do SLA */
export const speakStatusChangeSLA = (paciente, status, tempo) =>
  speak(applyTemplate('status_change_sla', { paciente, status, tempo }));

/** Status avançou mas SLA já estava vencido */
export const speakStatusChangeSLAVencido = (paciente, status, tempo) =>
  speak(applyTemplate('status_change_sla_vencido', { paciente, status, tempo }));

/** Avaliação confirmada pela profissional */
export const speakAvaliacaoConfirmada = (paciente, profissional) =>
  speak(applyTemplate('avaliacao_confirmada', { paciente, profissional }));

/** Alerta de SLA se aproximando — "Atenção, faltam X horas para vencer o SLA do Paciente João" */
export const speakSLAAlert = (paciente, tempo) =>
  speak(applyTemplate('sla_alerta', { paciente, tempo }));

/** SLA vencido — "SLA do Paciente João vencido há X horas" */
export const speakSLAVencido = (paciente, tempo) =>
  speak(applyTemplate('sla_vencido', { paciente, tempo }));

/** Paciente implantado */
export const speakImplantado = (paciente) =>
  speak(applyTemplate('implantado', { paciente }));
